import clsx from 'clsx';

import * as styles from './SuggestionDetails.sty.js';
import { TextEllipsisAroundChildren } from '../../../core/components/TextEllipsisAroundChildren.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../../core/types/data-props.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { WithChildren } from '../../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../core/utils/can-receive-default-text-format.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { Text } from '../../../typography/text/Text.js';

/**
 * Accepted properties for FilterField suggestion details.
 * @public
 */
export type FilterFieldSuggestionDetailsProps = WithChildren &
  StylingProps &
  DataTestId &
  BehaviorTrackingProps;

/**
 * The `FilterField.SuggestionDetails` can be used to render additional
 * information for a suggestion.
 * @public
 */
export const SuggestionDetails = (props: FilterFieldSuggestionDetailsProps) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;
  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);
  const childIsText = _canReceiveDefaultTextFormat(children);

  return (
    <Flex
      className={clsx(styles.suggestionDetails, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      {...behavioralTrackingProps}
      alignItems="center"
      paddingY={2}
    >
      {childIsText ? (
        <Text textStyle="small" className={styles.suggestionDetailsText}>
          <TextEllipsisAroundChildren>{children}</TextEllipsisAroundChildren>
        </Text>
      ) : (
        <Text as="div" textStyle="small">
          {children}
        </Text>
      )}
    </Flex>
  );
};

SuggestionDetails.displayValue = 'FilterField.SuggestionDetails';
